import type { Lookups, RelationshipLabel } from '@/lib/types';

export interface TypeGroup<T> {
  key: string;
  /** The Relationship_Type name; the primary bucket carries the first type's name ("Primary Family") */
  label: string;
  primary: boolean;
  items: T[];
}

/**
 * People bucketed by Relationship_Type in MP's own sort order. The first
 * type (Primary Family) is the default bucket; anything else in the list
 * (Extended Family, In-Laws, Business & Professional) is its own bucket so
 * a niece or a supervisor never reads as a daughter. Labels only make sense
 * when there is more than one bucket (a lone "Primary Family" over a
 * family of four says nothing), so callers show them when `length > 1`.
 * People without a relationship yet sit with the primary bucket, where the
 * nudge to say how they are related is.
 */
export function groupByRelationshipType<T>(
  items: T[],
  rel: (item: T) => RelationshipLabel | null | undefined,
  lookups: Lookups
): TypeGroup<T>[] {
  const types = [...lookups.relationshipTypes].sort((a, b) => a.sortOrder - b.sortOrder);
  const primaryId = types[0]?.id ?? null;
  const buckets = new Map<number | null, T[]>();
  for (const item of items) {
    const r = rel(item);
    const id = r?.typeId ?? null;
    const key = id === null || id === primaryId ? null : id;
    const list = buckets.get(key) ?? [];
    list.push(item);
    buckets.set(key, list);
  }
  const out: TypeGroup<T>[] = [];
  const primary = buckets.get(null);
  if (primary)
    out.push({ key: 'primary', label: types[0]?.name ?? 'Family', primary: true, items: primary });
  for (const t of types) {
    if (t.id === primaryId) continue;
    const list = buckets.get(t.id);
    if (list) out.push({ key: String(t.id), label: t.name, primary: false, items: list });
  }
  // A type MP knows on the row but not in the lookups (should not happen): still shown, still labelled.
  for (const [id, list] of buckets) {
    if (id === null || types.some((t) => t.id === id)) continue;
    const name = list.map(rel).find((r) => r?.typeName)?.typeName ?? 'Other';
    out.push({ key: String(id), label: name, primary: false, items: list });
  }
  return out;
}
